import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Link,
  CardActionArea,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  SmartToy as AIIcon,
  Star as StarIcon,
  MenuBook as GuideIcon,
} from '@mui/icons-material';
import { useRecipe } from '../context/RecipeContext';
import landingBackground from '../images/landingbackground3.jpg';

const Resources: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  const nutritionResources = [
    {
      name: "Nutrition.gov",
      url: "https://nutrition.gov",
      description: "A USDA-sponsored gateway offering science-based resources on healthy eating, food safety, physical activity, and dietary supplements, segmented by audience (e.g., infants to seniors)."
    },
    {
      name: "Academy of Nutrition and Dietetics",
      url: "https://eatright.org",
      description: "The world's largest organization of registered dietitians, providing expert-backed recommendations, recipes, and policy insights on nutrition for various health conditions."
    },
    {
      name: "The Nutrition Source (Harvard T.H. Chan School of Public Health)",
      url: "https://nutritionsource.hsph.harvard.edu",
      description: "Evidence-based articles, FAQs, and tools like the Healthy Eating Plate for translating nutrition science into practical daily eating habits."
    },
    {
      name: "USDA Food and Nutrition Information Center",
      url: "https://fnic.nal.usda.gov",
      description: "A comprehensive USDA hub with dietary reference intakes (DRIs), nutrient databases, and links to federal guidelines for balanced diets and food security."
    },
    {
      name: "MedlinePlus (National Library of Medicine)",
      url: "https://medlineplus.gov/nutrition.html",
      description: "NIH-curated health information on diets, vitamins, and conditions, with easy-to-understand articles, videos, and links to clinical trials."
    },
    {
      name: "Office of Dietary Supplements (NIH)",
      url: "https://ods.od.nih.gov",
      description: "Detailed, research-driven factsheets on vitamins, minerals, and supplements, including daily needs, sources, and interactions for safe use."
    },
    {
      name: "Mayo Clinic Nutrition Center",
      url: "https://mayoclinic.org/healthy-lifestyle/nutrition-and-healthy-eating",
      description: "Medical-grade advice on meal planning, weight management, and disease-specific diets, backed by Mayo's clinical expertise."
    },
    {
      name: "Linus Pauling Institute Micronutrient Information Center",
      url: "https://lpi.oregonstate.edu/mic",
      description: "Oregon State University resource with in-depth, scientifically accurate info on vitamins, minerals, and phytochemicals for disease prevention and health promotion."
    },
    {
      name: "Centers for Disease Control and Prevention (CDC) Nutrition",
      url: "https://cdc.gov/nutrition",
      description: "Public health-focused data on dietary patterns, obesity prevention, and community programs, including statistics and guidelines for healthy lifestyles."
    }
  ];

  const aiInnovationSites = [
    {
      name: "OpenAI ChatGPT",
      url: "https://chat.openai.com",
      description: "Advanced conversational AI that can assist with writing, research, coding, and creative tasks."
    },
    {
      name: "Finster Chat",
      url: "https://www.finsterchat.com",
      description: "AI financial assistant providing 24/7 personalized guidance on wealth management, investment strategies, debt management, and retirement planning."
    }, 
    {
      name: "GitHub Copilot",
      url: "https://github.com/features/copilot",
      description: "AI-powered code completion tool that helps developers write code faster and more efficiently."
    },
    {
      name: "AI Stock Tickers",
      url: "https://aistocktickers.com",
      description: "Stock analysis, trading, and AI-powered insights featuring current market data, interactive charts, and personalized investment tools."
    },   
    {
      name: "Midjourney",
      url: "https://midjourney.com",
      description: "AI-powered image generation tool that creates stunning artwork from text descriptions."
    },
    {
      name: "AI Wisdom Council",
      url: "https://www.aiwisdomcouncil.com",
      description: "AI-powered platform simulating voices and perspectives of renowned philosophers, scientists, and leaders to provide timeless wisdom and insights."
    },
    {
      name: "Ideal Match AI",
      url: "https://www.idealmatchai.com/",
      description: "IdealMatchAI is an AI-powered platform that helps users generate a romantic match based on their personalized profile in minutes. It does this by combining self-reflection with advanced character creation."
    },
    {
      name: "Evolution Explorer",
      url: "https://www.aievolutionexplorer.com",
      description: "Educational platform exploring the theory of evolution, explaining how species adapt, how new species form, and the diversity of life on Earth."
    },
    {
      name: "Runway ML",
      url: "https://runwayml.com",
      description: "Creative AI tools for video editing, image generation, and content creation."
    },
    {
      name: "Anthropic Claude",
      url: "https://anthropic.com/claude",
      description: "Advanced AI assistant focused on being helpful, honest, and safe for various applications."
    },
    {
      name: "Stability AI",
      url: "https://stability.ai",
      description: "Open-source AI models for image generation and other creative applications."
    },
    {
      name: "Alternate History AI",
      url: "https://althistai.com",
      description: "Interactive AI-powered platform for exploring alternate historical timelines, analyzing real events, and simulating outcomes by changing key decisions."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Resources - Trusted Nutrition & AI Innovation Sites | Create A Dish</title>
        <meta name="description" content="Explore trusted sources for nutritional information and innovative AI applications. Discover expert nutrition guides, AI-powered tools, and educational resources for cooking and healthy eating." />
        <meta name="keywords" content="nutrition resources, AI tools, cooking resources, dietary information, nutrition guides, AI applications, food resources, healthy eating, recipe resources" />
        <link rel="canonical" href="https://createadish.com/resources" />
        <meta property="og:title" content="Resources - Trusted Nutrition & AI Innovation Sites" />
        <meta property="og:description" content="Explore trusted sources for nutritional information and innovative AI applications." />
        <meta property="og:url" content="https://createadish.com/resources" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Resources - Trusted Nutrition & AI Innovation Sites" />
        <meta name="twitter:description" content="Explore trusted sources for nutritional information and innovative AI applications." />
      </Helmet>
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
      {/* Hero Section */}
      <Box
        sx={{
          height: { xs: '40vh', md: '50vh' },
          backgroundImage: `url(${landingBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 2,
            }}
          >
            Resources
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              mb: 1,
            }}
          >
            Trusted Sources for Nutrition & AI Innovation
          </Typography>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
        <Typography
          variant="h4"
          component="h2"
          sx={{
            textAlign: 'center',
            mb: 4,
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          Helpful Resources
        </Typography>

        <Typography
          variant="body1"
          sx={{
            mb: 4,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1rem', md: '1.1rem' }
          }}
        >
          Discover reliable sources for nutritional information and explore innovative AI applications
          that are transforming how we work, create, and learn.
        </Typography>

        {/* Our Guides Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              textAlign: 'center',
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <GuideIcon />
            Our Comprehensive Guides
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 3,
              mb: 6
            }}
          >
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardActionArea onClick={() => navigate('/kitchen-toolkit')} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    The Essential Kitchen Toolkit
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Must-have gear for every home chef—from chef's knives to cast iron skillets.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardActionArea onClick={() => navigate('/dietary-guide')} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    The Ultimate Dietary Guide
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Tailoring AI recipes to your lifestyle—Keto, Vegan, Paleo, and more.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardActionArea onClick={() => navigate('/global-cuisine')} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Global Cuisine 101
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    A world of flavors at your fingertips—explore international cuisines.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardActionArea onClick={() => navigate('/culinary-glossary')} sx={{ height: '100%' }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    The Culinary Glossary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    Master every cooking technique—your dictionary of culinary terms.
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        </Box>

        {/* Nutrition Resources Section */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              textAlign: 'center',
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <RestaurantIcon />
            Reliable Websites for Nutritional and Dietary Information
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3
            }}
          >
            {nutritionResources.map((resource, index) => (
              <Card key={index} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {resource.name}
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {resource.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* AI Innovation Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              textAlign: 'center',
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <AIIcon />
            Innovative AI Applications
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 3
            }}
          >
            {aiInnovationSites.map((site, index) => (
              <Card key={index} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: 3, flexGrow: 1 }}>
                  <Typography variant="h6" component="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                    <Link
                      href={site.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {site.name}
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {site.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            component="h3"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            Ready to Create Your Perfect Dish?
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<StarIcon />}
            onClick={handleGetStarted}
            sx={{
              minWidth: 200,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
            }}
          >
            Get Started
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
          </Typography>
        </Box>
      </Box>
    </Box>
    </>
  );
};

export default Resources;